<template>
    <div>
        <alerts :_success="success" :_error="error"></alerts>

        <!-- Skeleton UI -->
        <div v-if="wait" class="mb-3 bg-white rounded-lg shadow-sm p-4">
            <div class="mb-4">
                <h4 class="font-weight-bold">View Request</h4>
            </div>

            <!-- Header Section -->
            <div class="d-flex align-items-center mb-3">
                <!-- Avatar -->
                <div class="border p-1 border-secondary rounded-circle">
                    <div class="bg-secondary blink" style="width: 50px; height: 50px; border-radius: 50%;"></div>
                </div>
                <div class="ml-3 w-100">
                    <!-- Name Placeholder -->
                    <div class="bg-secondary rounded w-50 blink" style="height: 15px;"></div>
                    <!-- Email Placeholder -->
                    <div class="bg-secondary rounded w-75 mt-2" style="height: 20px;"></div>
                </div>
            </div>
            <div class="mt-4 d-flex">
                <div class="flex-fill d-flex flex-column mr-2">
                    <div v-for="i in 8" :key="i" style="height: 20px" class="border bg-light rounded blink mb-2"></div>
                </div>
                <div class="flex-fill d-flex flex-column">
                    <div class="bg-light border rounded blink mb-1 flex-grow-1"></div>
                </div>
            </div>
            <div class="bg-secondary rounded  mt-2 blink" style="height: 10px;"></div>
        </div>
        <!-- End of skeleton UI -->

        <div class="row" v-if="request">
            <div class="col-md-7">
                <div class="p-4 bg-white shadow-sm rounded-lg mb-3">
                    <h5 class="font-weight-bold">Request Details</h5>

                    <div class="mb-3 d-flex align-items-center my-2 bg-light p-3" style="gap: 4px">
                        <div>
                            <img :src="request.user.avatar" class="rounded-circle p-1 border"
                                 style="height: 60px;border-color: #42B4FF!important;">
                        </div>
                        <div class="flex-fill">
                            <h6 class="font-weight-bold mb-0">{{ request.user.name }}</h6>
                            <span class="text-muted">{{ request.user.email }}</span> <br>

                            <div v-if="request.report" class="mt-2">
                                <a :href="request.report.url" target="_blank"
                                   class="btn btn-dark">
                                    Download Report
                                </a>
                            </div>
                        </div>
                    </div>

                    <div class="narration mb-1">
                        <p>{{ request.title }}</p>
                        <sup v-if="request.type=='card'" class="badge badge-secondary"><i>Card</i></sup>
                        <sup v-if="request.type=='advance'" class="badge small badge-warning"><i>Advance</i></sup>
                    </div>

                    <div class="btn-group my-4 btn-block">
                        <router-link v-if="request.type==='card'" :to="'/requests/card/'+request.code"
                                     class="btn btn-outline-dark">
                            Budgeting
                        </router-link>
                        <router-link v-else :to="'/requests/budgeting/'+request.code" class="btn btn-outline-secondary">
                            Budgeting
                        </router-link>
                        <router-link :to="'/requests/'+request.code +'/documents'" class="btn btn-outline-dark">
                            Documents <sup>{{ request.documents.length }}</sup>
                        </router-link>
                    </div>

                    <stage :request="request" class="mb-4 w-100"></stage>

                    <div v-if="request.is_draft" class="alert alert-warning font-weight-bold">
                        <p>This is a draft ERP request.</p>
                        <button class="btn btn-info" @click="publishErp()">Publish this ERP</button>
                    </div>

                    <table class="table table-borderless table-sm mb-4">
                        <tr>
                            <th style="width: 40%">Date Requested</th>
                            <td>{{ day(request.created_at).format('MMM, DD YYYY H:mm') }}</td>
                        </tr>
                        <tr>
                            <th>Request Code</th>
                            <td class="fw-bold">{{ request.code }}</td>
                        </tr>
                        <tr>
                            <th>Requested Amount</th>
                            <td>
                                <b>{{ request.currency }}</b>&nbsp;
                                <span class="font h5 text-teal">{{ request.amount }}</span>

                                <template v-if="request.refundAmount > 0">
                                    (<span class="text-danger">Refund(s) -${{ request.refundAmount }}</span>) <br>
                                </template>
                            </td>
                        </tr>
                        <tr>
                            <th>Status</th>
                            <td>
                                <span :class="`status ${request.status}`">{{ request.status }}</span>
                                <span v-if="request.isAcknowledged" class="text-muted">
                                    ({{ day(request.acknowledged_at).format('MMM, DD YYYY H:mm') }})
                                </span>
                            </td>
                        </tr>
                        <tr>
                            <th>Expected Date</th>
                            <td>{{ day(request.date).format('MMM, DD YYYY') }}</td>
                        </tr>
                        <tr>
                            <th>Tagged Users</th>
                            <td v-if="request.tagged">
                                <div v-if="request.tagged.length > 0">
                                    <tagged class="mb-1" :request="request"></tagged>
                                    <br>
                                    <router-link :to="`/staff/${u.id}`" v-for="u in request.tagged" :key="u.id"
                                                 class="badge badge-info mr-1">{{ u.name }}
                                    </router-link>
                                </div>
                                <div v-else class="text-muted">No users tagged on this erp</div>
                            </td>
                        </tr>
                    </table>

                    <template v-if="!request.isTagged && request.status=='pending'">
                        <router-link :to="'/requests/budgeting/'+ request.code" class="btn btn-dark mr-1">
                            Edit Request
                        </router-link>
                        <button @click="deleteRequest()" class="btn btn-danger mr-1">Delete</button>
                    </template>

                    <div v-if="request.request" class="my-4 border bg-light p-3 rounded-lg">
                        <router-link :to="`/requests/${request.request.code}`" target="_blank" class="text-dark">
                            <h6 class="font-weight-bold">Topup Request</h6>
                            Code <b>{{ request.request.code }} </b> - ${{ request.request.amount }}
                            <p>{{ request.request.title }}</p>
                            <span class="status" :class="request.request.status">{{ request.request.status }}</span>
                            &nbsp;
                            <span class="text-muted">{{
                                    day(request.request.created_at).format('MMM, DD YYYY H:mm')
                                }}</span>
                        </router-link>
                    </div>

                    <div v-if="request.parent" class="my-4 border bg-light p-3 rounded-lg">
                        <router-link :to="`/requests/${request.parent.code}`" target="_blank" class="text-dark">
                            <h6 class="font-weight-bold">Parent Request</h6>
                            Code <b>{{ request.parent.code }} </b> - ${{ request.parent.amount }}
                            <p>{{ request.parent.title }}</p>
                            <span class="status" :class="request.parent.status">{{ request.parent.status }}</span>
                            &nbsp;
                            <span class="text-muted">{{
                                    day(request.parent.created_at).format('MMM, DD YYYY H:mm')
                                }}</span>
                        </router-link>
                    </div>
                </div>

                <!-- Decline -->
                <div v-if="request.isDeclined" class="p-4 bg-white shadow-sm">
                    <h4 class="font-weight-bold">Review Your Request</h4>
                    <p class="text-muted">Considering that your request has been declined, you have the option to either
                        review or discard it.</p>

                    <router-link :to="'/requests/create/'+ request.id" class="btn btn-success mr-2">
                        Review Request
                    </router-link>
                    <button @click="deleteRequest()" class="btn btn-danger">
                        Delete Request
                    </button>
                </div>

                <!-- Disbursement -->
                <div v-if="request.user_id == auth.id && request.isDisbursed"
                     class="p-4 bg-white rounded-lg shadow-sm mb-3">
                    <h5 class="font-weight-bold">Close Request</h5>

                    <form v-if="!request.isAcknowledged && request.status=='disbursed'"
                          @submit.prevent="sendAcknowledement()">
                        <p class="text-muted">
                            Acknowledge receipt of the requested amount <br>
                            This will close the request.
                        </p>

                        <p v-if="success" class="alert alert-success">{{ success }}</p>

                        <div class="custom-control custom-switch mb-2">
                            <input type="radio" class="custom-control-input" :value="true" v-model="acknowledge_state"
                                   id="yes" :disabled="acknowledging">
                            <label class="custom-control-label" for="yes">
                                I received the requested amount <br>
                                <small class="text-muted">This will close the request</small>
                            </label>
                        </div>

                        <div class="custom-control custom-switch text-danger mb-3">
                            <input type="radio" class="custom-control-input" :value="false" v-model="acknowledge_state"
                                   id="nope" :disabled="acknowledging">
                            <label class="custom-control-label" for="nope">
                                I did not received the requested amount <br>
                                <small class="text-muted">This closes the request</small>
                            </label>
                        </div>
                        <button :class="(acknowledge_state===true ? 'btn btn-success' : 'btn btn-danger')"
                                :disabled="acknowledging">
                            <template v-if="acknowledging">Sending Response...</template>
                            <template v-else>Send Response</template>
                        </button>
                    </form>

                    <div v-if="request.isAcknowledged">
                        <p>You acknowledged receipt of $<span class="font">{{ request.amount }}</span></p>
                        <b>Disbursement Date: </b> {{ day(request.disbursed_at).format('MMM, DD YYYY H:mm') }} <br>
                        <b>Disbursed By: </b> {{ request.approval.disbursed_by.name }} <br>
                        <b>Acknowledge at: </b> {{ day(request.acknowledged_at).format('MMM, DD YYYY H:mm') }}
                    </div>
                </div>

                <!-- Comments -->
                <div class="p-3 bg-white rounded-lg shadow-sm mb-3">
                    <h5 class="font-weight-bold">Comments</h5>

                    <div v-if="!request.comments.length" class="mb-3 text-muted">
                        No comments
                    </div>

                    <div class="mb-3">
                    <textarea rows="2" placeholder="Enter your comment here..."
                              ref="commentBox"
                              v-model="comment.comment" class="form-control mb-2"></textarea>
                        <button class="btn btn-dark btn-sm" @click="leaveComment()"
                                :disabled="!comment.comment || commenting">
                            <span v-if="commenting" class="spinner-border spinner-border-sm"></span>
                            Submit
                        </button>
                    </div>

                    <div v-for="c in request.comments" :key="c.id"
                         class="mb-3 p-2 rounded-lg d-flex comment">
                        <div>
                            <img :src="c.user.avatar" class="rounded-circle p-1 border" style="height: 48px">
                        </div>
                        <div>
                            <h6 class="font-weight-bold mb-1">
                                {{ c.user.name }} &nbsp;
                                <small class="text-muted">{{ day(c.created_at).format('MMM, DD YYYY H:mm A') }}</small>
                            </h6>

                            <p class="mb-0">{{ c.comment }}</p>
                            <div v-if="c.user_id === getAuth.id">
                                <a href="javascript:void(0)" @click="editComment(c)">Edit</a>&nbsp;
                                <a href="javascript:void(0)" @click="deleteComment(c)" class="text-danger">Delete</a>
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            <div class="col-md-5">
                <div class="bg-white rounded-lg p-3 mb-3">
                    <h5 class="font-weight-bold">Approval Timeline</h5>

                    <!--  Creation-->
                    <section>
                        <div class="activity-item">
                            <div class="circle active">
                                {{ day(request.created_at).format('MMM, DD') }}
                            </div>
                            <div class="ml-2 py-3">
                                <h6 class="mb-1 font-weight-bold">Request Approval</h6>
                                <p class="mb-0 text-muted">Request created</p>
                                <ul class="bulletin">
                                    <li><strong>Date Created:</strong> {{
                                            day(request.created_at).format('MMM, DD YYYY H:mm')
                                        }}
                                    </li>
                                    <li><strong>Requesting User:</strong> {{ request.user.name }}</li>
                                </ul>
                            </div>
                        </div>
                    </section>

                    <!--  Authorization-->
                    <section>
                        <div v-if="request.isAuthorized" class="activity-item">
                            <div class="circle active">
                                {{ day(request.approval.authorized_at).format('MMM, DD') }}
                            </div>
                            <div class="ml-2 py-2">
                                <h6 class="mb-1 font-weight-bold">Authorize Request</h6>
                                <p class="mb-0 text-muted">Request is Authorized</p>
                                <ul class="bulletin">
                                    <li><strong>Date Authorized:</strong> {{
                                            day(request.approval.authorized_at).format('MMM, DD YYYY H:mm')
                                        }}
                                    </li>
                                    <li><strong>Authorized By:</strong> {{ request.approval.authorized_by.name }}</li>

                                </ul>
                            </div>
                        </div>

                        <div v-else class="activity-item">
                            <div class="circle">
                            </div>
                            <div class="ml-2 py-2">
                                <p class="mb-0 font-weight-bold">Request Authorization</p>
                                <div class="text-sm">
                                    Once your request is processed, you will receive notifications via email, Flock, and
                                    within this app under
                                    <router-link to="/notifications">notifications page</router-link>
                                </div>
                            </div>
                        </div>
                    </section>

                    <!--  Approval -->
                    <section>
                        <div v-if="request.isApproved" class="activity-item">
                            <div class="circle active">
                                {{ day(request.approval.authorized_at).format('MMM, DD') }}
                            </div>
                            <div class="ml-2 py-2">
                                <h6 class="mb-1 font-weight-bold">Approve Request</h6>
                                <p class="mb-0 text-muted">Request was approved</p>
                                <ul class="bulletin">
                                    <li><strong>Date Approved:</strong>
                                        {{ day(request.approval.authorized_at).format('MMM, DD YYYY H:mm') }}
                                    </li>
                                    <li><strong>Approval Remarks:</strong> {{ request.approval.approval_remarks }}</li>
                                    <li><strong>Approved By:</strong> {{ request.approval.approved_by.name }}</li>
                                </ul>
                            </div>
                        </div>
                        <div v-else class="activity-item">
                            <div class="circle">
                            </div>
                            <div class="ml-2 py-3">
                                <p class="mb-0 font-weight-bold">Approval</p>
                                <div class="text-sm">
                                    Waiting second approval
                                </div>
                            </div>
                        </div>
                    </section>

                    <!--  Verification -->
                    <section>
                        <div v-if="request.isVerified" class="activity-item">
                            <div class="circle active">
                                {{ day(request.approval.verified_at).format('MMM, DD') }}
                            </div>
                            <div class="ml-2 py-2">
                                <h6 class="mb-1 font-weight-bold">Verify Request</h6>
                                <p class="mb-0 text-muted">Request was verified</p>
                                <ul class="bulletin">
                                    <li><strong>Date Verified:</strong>
                                        {{ day(request.approval.verified_at).format('MMM, DD YYYY H:mm') }}
                                    </li>
                                    <li><strong>Verified By:</strong> {{ request.approval.verified_by.name }}</li>
                                </ul>
                            </div>
                        </div>
                        <div v-else class="activity-item">
                            <div class="circle">
                            </div>
                            <div class="ml-2 py-3">
                                <p class="mb-0 font-weight-bold">Verification</p>
                                <div class="text-sm">
                                    Waiting verification
                                </div>
                            </div>
                        </div>
                    </section>

                    <!--  Disbursement -->
                    <section>
                        <div v-if="request.isDisbursed" class="activity-item">
                            <div class="circle active">
                                {{ day(request.approval.disbursed_at).format('MMM, DD') }}
                            </div>
                            <div class="ml-2 py-2">
                                <h6 class="mb-1 font-weight-bold">Disburse Request</h6>
                                <p class="mb-0 text-muted">Request disbursed</p>
                                <ul class="bulletin">
                                    <li><strong>Date Disbursed:</strong>
                                        {{ day(request.approval.disbursed_at).format('MMM, DD YYYY H:mm') }}
                                    </li>
                                    <li><strong>Disbursed By:</strong> {{ request.approval.disbursed_by.name }}</li>
                                </ul>
                            </div>
                        </div>
                        <div v-else class="activity-item">
                            <div class="circle">
                            </div>
                            <div class="ml-2 py-3">
                                <p class="mb-0 font-weight-bold">Disbursement</p>
                                <div class="text-sm">
                                    Waiting Disbursement
                                </div>
                            </div>
                        </div>
                    </section>


                    <!--  Closing -->
                    <section>
                        <div v-if="request.isAcknowledged" class="activity-item">
                            <div class="circle active">
                                {{ day(request.acknowledged_at).format('MMM, DD') }}
                            </div>
                            <div class="ml-2 py-2">
                                <h6 class="mb-1 font-weight-bold">Acknowledgement</h6>
                                <p class="mb-0 text-muted">Received funds</p>
                                <ul class="bulletin">
                                    <li><strong>Date Acknowledged:</strong>
                                        {{ day(request.acknowledged_at).format('MMM, DD YYYY H:mm') }}
                                    </li>
                                    <li><strong>Acknowledgement Status:</strong> Received</li>
                                </ul>
                            </div>
                        </div>
                        <div v-else class="activity-item">
                            <div class="circle">
                            </div>
                            <div class="ml-2 py-3">
                                <p class="mb-0 font-weight-bold">Acknowledgement</p>
                                <div class="text-sm">
                                    Not acknowledged
                                </div>
                            </div>
                        </div>
                    </section>

                    <!--  Declined -->
                    <section>
                        <div v-if="request.isDeclined" class="activity-item">
                            <div class="circle active">
                                {{ day(request.approval.declined_at).format('MMM, DD') }}
                            </div>
                            <div class="ml-2 py-2">
                                <h6 class="mb-1 font-weight-bold">Decline Request</h6>
                                <p class="mb-0 text-danger">Request was declined</p>
                                <ul class="bulletin">
                                    <li><strong>Date Declined:</strong>
                                        {{ day(request.approval.declined_at).format('MMM, DD YYYY H:mm') }}
                                    </li>
                                    <li><strong>Reason:</strong> {{ request.approval.decline_remarks }}</li>
                                    <li><strong>Declined By:</strong> {{ request.approval.declined_by.name }}</li>
                                </ul>
                            </div>
                        </div>
                    </section>
                </div>

            </div>
        </div>
    </div>
</template>

<script>
import Alerts from '@/components/Alerts'
import Tagged from './helpers/Tagged'
import Stage from './Stage'

export default {
    name: 'ViewRequest',
    components: {Stage, Tagged, Alerts},
    props: ['code', 'auth'],
    data() {
        return {
            file: '',
            uploading: false,

            request: {documents: [], comments: [], user: {}, approval: {}},
            requests: [],
            acknowledging: false,
            acknowledge_state: true,

            comment: {
                commend_id: null,
                comment: '',
            },
            commenting: false
        }
    },
    mounted() {
        this.loadRequest()
    },
    computed: {
        getAuth() {
            return (this.auth)
        }
    },
    methods: {
        handleFileUpload(event) {
            this.file = event.target.files[0]
        },
        uploadFile() {
            this.clear()
            this.uploading = true
            let formData = new FormData()
            formData.append('file', this.file)

            this.$http.post('/request/' + this.code + '/docs', formData, {
                    headers: {'Content-Type': 'multipart/form-data'}
                }
            ).then(() => {
                this.success = 'File uploaded successfully!'
                this.$refs.file.value = null
                setTimeout(() => (this.loadRequest()), 1000)

            }).catch(error => {
                this.showError(error)
            }).then(() => {
                this.uploading = false
            })
        },
        leaveComment() {
            this.commenting = true
            this.$http.post('/request/' + this.code + '/comments', {
                comment: this.comment.comment,
                id: this.request.id,
                comment_id: this.comment.commend_id
            }).then(() => {
                this.loadRequest()
                this.comment = {
                    commend_id: null,
                    comment: ''
                }
            }).catch(error => {
                this.showError(error)
            }).then(() => {
                this.commenting = false
            })
        },
        editComment(record) {
            console.log(record)
            this.comment.comment = record.comment
            this.comment.commend_id = record.id

            this.$nextTick(() => {
                this.$refs.commentBox.scrollIntoView({
                    behavior: "smooth",
                    block: "center"
                })

                this.$refs.commentBox.focus()
            })
        },
        deleteComment(record) {
            if (confirm('Are you sure you want to delete this comment?')) {
                this.success = 'Please wait...'
                this.$http.post('/request/' + this.code + '/comments', {
                    id: record.id,
                    delete_id: record.id
                }).then(() => {
                    this.loadRequest()
                    this.success = 'Comment deleted successfully!'
                }).catch(error => {
                    this.showError(error)
                }).then(() => {
                    this.acknowledging = false
                })
            }
        },
        loadRequest() {
            this.wait = true
            this.clear()
            this.$http.get('/request/' + this.code).then(response => {
                this.request = response.data.data
            }).catch(error => {
                this.showError(error)
            }).then(() => {
                this.wait = false
            })
        },
        publishErp() {
            if (confirm('Are you sure you want to publish this request?')) {
                this.acknowledging = true

                this.$http.post('/request/' + this.code + '/publish').then(() => {
                    this.success = 'Request published successfully!'
                    this.loadRequest()
                }).catch(error => {
                    this.showError(error)
                }).then(() => {
                    this.acknowledging = false
                })
            }
        },
        deleteRequest() {
            if (confirm('Are you sure you want to delete this request?')) {
                this.success = 'Please wait...'
                window.scroll(0, 0)
                this.clear()
                this.$http.post('/request/' + this.code + '/delete').then(() => {
                    this.success = 'Request deleted successfully!'
                    setTimeout(() => (window.location.href = '/requests'), 600)
                }).catch(error => {
                    this.showError(error)
                }).then(() => {
                    this.acknowledging = false
                })
            }
        },
        sendAcknowledement() {
            this.acknowledging = true
            this.clear()
            window.scroll(0, 0)
            this.success = 'Sending your response, please wait...'
            this.$http.post('/request/' + this.code + '/acknowledge', {state: this.acknowledge_state}).then(() => {
                this.loadRequest()
                this.success = 'Response sent. Thank you.'
                setTimeout(() => (this.success = ''), 600)
            }).catch(error => {
                this.showError(error)
            }).then(() => {
                this.acknowledging = false
            })
        }
    }
}
</script>

<style scoped>
.docs td {
    border-top: 1px dotted #dddddd;
}

.comment {
    column-gap: 8px;
}

.comment:not(:last-child) {
    border-bottom: 1px dotted #ddd;
}

.circle {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background-color: white;
    font-size: 10px;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    z-index: 1;
    border: 1px solid #ddd;
    color: #42B4FF;
}

.activity-item {
    position: relative;
    padding-left: 60px;
}

.circle.active {
    border-color: #ddd;
}

.activity-item::before {
    content: '';
    position: absolute;
    left: 24px;
    top: 0;
    bottom: 0;
    width: 2px;
    background-color: #eee;
    z-index: 0;
}

.activity-item .circle {
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1;
}

ul.bulletin {
    list-style: none; /* Remove default list style */
    margin: 10px 0 0 30px;
    padding-bottom: 15px;
    border-bottom: 1px dotted #ddd;
    font-size: 12px;
}

ul.bulletin li::before {
    content: '- '; /* Custom bullet */
    color: #96c48d; /* Bullet color */
}

</style>